import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COLLECTIONS = [
  { name: "Gifts", img: "https://images.pexels.com/photos/12080066/pexels-photo-12080066.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", to: "/shop?category=Gifts" },
  { name: "Toys", img: "https://images.pexels.com/photos/32720288/pexels-photo-32720288.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", to: "/shop?category=Toys" },
  { name: "Decor", img: "https://images.unsplash.com/photo-1719122180607-66d0c7d5c26c?crop=entropy&cs=srgb&fm=jpg&q=85&w=940", to: "/shop?category=Decor" },
  { name: "Stationery", img: "https://images.unsplash.com/photo-1590708024614-4edd2104a840?crop=entropy&cs=srgb&fm=jpg&q=85&w=940", to: "/shop?category=Stationery" },
];

export default function Home() {
  const [bestsellers, setBestsellers] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/products", { params: { bestseller: true } }).then(({ data }) => setBestsellers(data.slice(0, 4)));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the list! 💛");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email");
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-blush overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
          <div className="fade-up">
            <p className="eyebrow">Thoughtfully gathered</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-[1.1]">
              Little luxuries,
              <br />
              wrapped with love.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-charcoal/70 max-w-md leading-relaxed">
              A soft, curated boutique of gifts, toys, decor &amp; stationery — the kind of pieces
              that make someone feel remembered.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild className="bg-charcoal text-white rounded-none h-12 px-8 hover:bg-gold tracking-wide font-display">
                <Link to="/shop" data-testid="hero-shop-btn">Explore Collections</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-none h-12 px-8 border-charcoal text-charcoal hover:bg-charcoal hover:text-white tracking-wide font-display">
                <Link to="/shop?bestseller=true" data-testid="hero-bestsellers-btn">Shop Best Sellers</Link>
              </Button>
            </div>
          </div>
          <div className="fade-up" style={{ animationDelay: "150ms" }}>
            <div className="aspect-[4/5] overflow-hidden rounded-sm border border-gold/25 shadow-[0_30px_60px_-30px_rgba(197,160,89,0.4)]">
              <img
                src="https://images.pexels.com/photos/4271686/pexels-photo-4271686.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Blush gift boxes"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Collections */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
        <div className="text-center mb-12">
          <p className="eyebrow">Browse by</p>
          <h2 className="mt-3 font-serif text-3xl lg:text-4xl text-charcoal">Our Collections</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {COLLECTIONS.map((c, i) => (
            <Link
              key={c.name}
              to={c.to}
              data-testid={`collection-${c.name.toLowerCase()}`}
              className="group relative aspect-[3/4] overflow-hidden rounded-sm fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <img src={imgUrl(c.img)} alt={c.name} className="w-full h-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                <h3 className="font-serif text-2xl">{c.name}</h3>
                <span className="eyebrow text-white/80 inline-flex items-center gap-1 mt-1">
                  Shop now <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Bestsellers */}
      <section className="bg-white/60 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow">Loved by many</p>
              <h2 className="mt-3 font-serif text-3xl lg:text-4xl text-charcoal">Best Sellers</h2>
            </div>
            <Link to="/shop" className="eyebrow text-charcoal hover:text-gold inline-flex items-center gap-1" data-testid="view-all-link">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
            {bestsellers.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Brand story */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <div className="aspect-[4/3] overflow-hidden rounded-sm order-2 lg:order-1">
          <img
            src="https://images.pexels.com/photos/20009868/pexels-photo-20009868.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=940"
            alt="Hand tying a silk bow"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="order-1 lg:order-2">
          <p className="eyebrow">Our little story</p>
          <h2 className="mt-3 font-serif text-3xl lg:text-4xl text-charcoal leading-tight">
            Every parcel leaves our hands the way we'd want to receive it.
          </h2>
          <p className="mt-6 text-charcoal/70 leading-relaxed">
            LuvSprout began with a simple belief: the small, beautiful things matter. We source
            pieces that feel considered — soft to the touch, quietly elegant, and endlessly
            giftable. Each order is wrapped by hand and sent with a note of care.
          </p>
          <Button asChild variant="outline" className="mt-8 rounded-none border-charcoal text-charcoal hover:bg-charcoal hover:text-white font-display tracking-wide">
            <Link to="/about" data-testid="about-link">Read more</Link>
          </Button>
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-blush-accent">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="eyebrow">Join the LuvSprout list</p>
          <h2 className="mt-3 font-serif text-3xl lg:text-4xl text-charcoal">
            First to know, first to gift.
          </h2>
          <p className="mt-4 text-charcoal/70">
            New arrivals, quiet little offers, and gifting inspiration — straight to your inbox.
          </p>
          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="h-12 rounded-none bg-white border-charcoal/20"
            />
            <Button
              type="submit"
              data-testid="newsletter-submit-btn"
              className="h-12 px-8 bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide"
            >
              Subscribe
            </Button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
